import React, { useState, useEffect } from 'react';
import classNames from 'classnames';
import FormElemWrapper from './FormElemWrapper';
import { IconWarning } from '../../Icons/IconWarning';
import { IconX } from '../../Icons/IconX';
import useFieldValues from 'hooks/useFormFieldsGetter';
import { FormattedFields } from 'hooks/useFormFieldsSharedLogic';

type InputFieldProps = {
  fields: FormattedFields;
  multiline?: boolean;
  description?: string;
  isError?: boolean;
  slots?: {
    leftIcon?: React.ReactNode;
  };
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChange: (value: string) => void;
  selectFirstFilterOption?: () => void;
  className?: string;
  descriptionClassName?: string;
  customError?: boolean;
};

const InputField: React.FC<InputFieldProps> = ({
  fields,
  multiline = false,
  description = '',
  slots = {},
  onFocus,
  onBlur,
  onChange,
  selectFirstFilterOption,
  className,
  descriptionClassName,
  customError = false,
}) => {
  const { label, hintMessage, tooltip } = useFieldValues(fields);
  const [isolatedValue, setIsolatedValue] = useState('');
  const [errorMessages, setErrorMessages] = useState<string | undefined>(undefined);

  const fieldAttrs = {
    name: fields.name?.value || '',
    placeholder: fields.placeholder?.value || '',
    value: fields?.value?.value || isolatedValue,
    required: !!fields.required?.value || false,
    type: fields.type?.value || 'text',
    conditional: fields.conditionalField?.value,
    relatesTo: fields.relatesTo?.value,
    id: fields.id,
  };

  useEffect(() => {
    setIsolatedValue(fieldAttrs.value);
  }, [fieldAttrs.value]);

  const resetField = () => {
    setIsolatedValue('');
    onChange('');
  };

  const onValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    setIsolatedValue(value);
    checkError();
    onChange(value);
  };

  const checkError = (event?: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const validity = event?.target?.validity;
    if (validity?.typeMismatch) {
      setErrorMessages(fields.errorMessages?.typeMismatch);
    } else if (fieldAttrs.required && event?.target.value.trim() === '') {
      setErrorMessages(fields.errorMessages?.valueMissing);
    } else if (validity?.valueMissing) {
      setErrorMessages(fields.errorMessages?.valueMissing);
    } else if (validity?.tooLong) {
      setErrorMessages(fields.errorMessages?.tooLong);
    } else if (validity?.tooShort) {
      setErrorMessages(fields.errorMessages?.tooShort);
    } else if (validity?.rangeOverflow) {
      setErrorMessages(fields.errorMessages?.rangeOverflow);
    } else if (validity?.rangeUnderflow) {
      setErrorMessages(fields.errorMessages?.rangeUnderflow);
    } else if (validity?.customError || customError) {
      setErrorMessages((fields.errorMessages as any)?.customError);
    } else {
      // TODO: validate based on regexp validations?
      setErrorMessages(undefined);
    }
  };

  useEffect(() => {
    checkError();
  }, [customError]);

  const isError = !!errorMessages;
  return (
    <FormElemWrapper
      className={classNames('form-element--input', className, {
        'form-element--filled': !!fieldAttrs.value,
        'conditional-field': fieldAttrs.conditional,
      })}
      isError={isError}
      slots={{
        fieldLabel: label ? <>{label}</> : null,
        description: description ? (
          <p className={`text-grey description ${descriptionClassName}`}>{description}</p>
        ) : null,
        tooltip: tooltip ? <>{tooltip}</> : null,
        error: isError ? <>{errorMessages}</> : null,
        hint: hintMessage ? <>{hintMessage}</> : null,
      }}
    >
      <span className="input-box">
        {slots.leftIcon && <span className="input-icon input-icon--left">{slots.leftIcon}</span>}

        <WrapperComponent
          value={isolatedValue}
          multiline={multiline}
          fieldAttrs={fieldAttrs}
          checkError={checkError}
          onFocus={onFocus}
          onBlur={onBlur}
          onChange={onValueChange}
          selectFirstFilterOption={selectFirstFilterOption}
        />
        {!!fieldAttrs.value.length && (
          <button
            type="button"
            className="input-action-btn input-icon input-action-btn--reset"
            aria-label="Clear"
            onClick={resetField}
          >
            <IconX />
          </button>
        )}
        {isError && (
          <span className="input-icon input-icon--right input-icon--error">
            <IconWarning />
          </span>
        )}
      </span>
    </FormElemWrapper>
  );
};

type WrapperComponentProps = {
  value: string;
  multiline: boolean;
  fieldAttrs: any;
  checkError: (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onChange: (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  selectFirstFilterOption?: () => void;
};

const WrapperComponent: React.FC<WrapperComponentProps> = ({
  value,
  multiline,
  fieldAttrs,
  checkError,
  onFocus,
  onBlur,
  onChange,
  selectFirstFilterOption,
}) => {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <Tag
      {...(multiline ? { rows: 3 } : { type: fieldAttrs.type })}
      value={value}
      name={fieldAttrs.name}
      id={fieldAttrs.id}
      placeholder={fieldAttrs.placeholder}
      required={fieldAttrs.required}
      maxLength={fieldAttrs.maxLength}
      minLength={fieldAttrs.minLength}
      min={fieldAttrs.min}
      max={fieldAttrs.max}
      aria-autocomplete={!multiline ? 'list' : undefined}
      className="field-text"
      onInput={onChange}
      onChange={(event) => {
        onChange?.(event);
      }}
      onBlur={(event) => {
        checkError(event);
        onBlur?.(event);
      }}
      onFocus={onFocus}
      onKeyDown={(event) => {
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          selectFirstFilterOption?.();
        }
      }}
      onInvalid={checkError}
    />
  );
};

export default InputField;
